 import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, Send, CheckCircle, Facebook, Instagram, MessageCircle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import brandLogo from '@/assets/logo.png';
import {
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  VIBER_CHAT_URL,
  WHATSAPP_CHAT_URL,
} from '@/constants/delivery';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.4, 0, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const CONTACT_INFO = [
  {
    icon: Phone,
    label: 'Telefon',
    value: CONTACT_PHONE_DISPLAY,
    sub: 'Dostupni svaki dan 08–18h',
    href: CONTACT_PHONE_TEL
  },
  {
    icon: MessageCircle,
    label: 'Viber',
    value: CONTACT_PHONE_DISPLAY,
    sub: 'Pišite nam poruku na Viberu',
    href: VIBER_CHAT_URL
  },
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    value: CONTACT_PHONE_DISPLAY,
    sub: 'Pišite nam poruku na WhatsAppu',
    href: WHATSAPP_CHAT_URL
  },
  {
    icon: MapPin,
    label: 'Lokacija',
    value: 'Izeta Čavića, Prijepolje',
    sub: 'Prijepolje, Srbija',
    href: 'https://maps.google.com/?q=Izeta+%C4%8Cavi%C4%87a,+Prijepolje'
  },
  {
    icon: Clock,
    label: 'Radno vrijeme',
    value: 'Pon - Sub: 08:00 – 18:00',
    sub: 'Nedeljom ne radimo',
    href: null
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error('Popunite obavezna polja.');
      return;
    }
    setSending(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!response.ok) throw new Error('Slanje poruke nije uspelo.');
      setSent(true);
      toast.success('Poruka je uspješno poslana!');
    } catch (error) {
      toast.error('Došlo je do greške. Pokušajte ponovo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* ── Hero banner ── */}
      <section className="relative bg-primary py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `url('${process.env.PUBLIC_URL}/brand/hero-pasture.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'left bottom',
          }} />
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 mb-5">
              <div className="h-px w-10 bg-gold/50" />
              <span className="text-gold/75 font-ui text-[11px] tracking-[0.3em] uppercase">Kontaktirajte nas</span>
              <div className="h-px w-10 bg-gold/50" />
            </motion.div>
            <motion.h1 variants={fadeUp}
              className="font-serif text-4xl sm:text-5xl font-bold text-primary-foreground leading-tight mb-5">
              Tu smo za Vas
            </motion.h1>
            <motion.p variants={fadeUp}
              className="font-garamond text-primary-foreground/65 text-lg leading-relaxed">
              Imate pitanje o naručivanju, dostavi ili nekim od naših proizvoda? Potencijalna saradnja?
              Javite nam se, rado ćemo pomoći.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Main content ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 xl:gap-16">

          {/* ── LEFT: Contact details ── */}
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true }} variants={stagger}
            className="lg:col-span-2 flex flex-col gap-6"
          >
            {/* Brand card */}
            <motion.div variants={fadeUp}
              className="bg-primary rounded-2xl p-7 shadow-elevated overflow-hidden relative">
              <div className="absolute bottom-0 right-0 select-none pointer-events-none overflow-hidden leading-none">
                <span className="font-serif font-black text-primary-foreground block"
                  style={{ fontSize: '9rem', opacity: 0.05, lineHeight: 0.8 }}>F</span>
              </div>
              <img
                src={brandLogo}
                alt="Fazlić"
                className="h-16 w-auto mb-5 object-contain"
              />
              <h3 className="font-serif text-primary-foreground text-lg font-semibold mb-2">
                Sjenički Proizvodi Fazlić
              </h3>
              <p className="font-garamond text-primary-foreground/55 text-sm leading-relaxed">
                Porodična firma koja proizvodi i nabavlja 100% domaće Sjeničke proizvode.
                Domaći, prirodni proizvodi prve klase od 2021. godine.
              </p>
              <Separator className="my-5 bg-primary-foreground/10" />
              <div className="flex items-center gap-3">
                <a href="https://facebook.com" target="_blank" rel="noreferrer"
                  className="h-9 w-9 rounded-full bg-primary-foreground/10 hover:bg-gold/20 flex items-center justify-center transition-colors duration-200">
                  <Facebook size={16} className="text-primary-foreground/70" />
                </a>
                <a href="https://instagram.com/sjenicki_proizvodii" target="_blank" rel="noreferrer"
                  className="h-9 w-9 rounded-full bg-primary-foreground/10 hover:bg-gold/20 flex items-center justify-center transition-colors duration-200">
                  <Instagram size={16} className="text-primary-foreground/70" />
                </a>
              </div>
            </motion.div>

            {/* Contact info items */}
            {CONTACT_INFO.map((item, i) => {
              const Icon = item.icon;
              const inner = (
                <motion.div key={item.label} variants={fadeUp}
                  className={`flex items-start gap-4 bg-card rounded-xl p-5 shadow-card border border-border
                              ${item.href ? 'hover:border-primary/40 hover:shadow-card-hover transition-all duration-200 cursor-pointer' : ''}`}
                >
                  <div className="h-10 w-10 rounded-xl bg-primary/8 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon size={18} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-ui text-xs text-muted-foreground uppercase tracking-wider mb-0.5">{item.label}</p>
                    <p className="font-ui text-sm font-semibold text-foreground">{item.value}</p>
                    <p className="font-ui text-xs text-muted-foreground mt-0.5">{item.sub}</p>
                  </div>
                </motion.div>
              );
              return item.href
                ? <a key={item.label} href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{inner}</a>
                : <div key={item.label}>{inner}</div>;
            })}
          </motion.div>

          {/* ── RIGHT: Contact form ── */}
          <motion.div
            initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="lg:col-span-3 scroll-mt-24"
            id="kontakt-forma"
          >
            <div className="bg-card rounded-2xl shadow-elevated border border-border p-8 md:p-10">

              {sent ? (
                /* ── Success state ── */
                <motion.div
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.45, ease: [0.34, 1.2, 0.64, 1] }}
                  className="flex flex-col items-center text-center py-10"
                >
                  <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                    <CheckCircle size={40} className="text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-foreground mb-3">
                    Poruka je poslana!
                  </h3>
                  <p className="font-garamond text-muted-foreground text-base leading-relaxed max-w-sm">
                    Hvala na poruci. Javićemo Vam se što je brže moguće.
                   
                  </p>
                  <button
                    onClick={() => { setSent(false); setForm({ name: '', phone: '', message: '' }); }}
                    className="mt-8 text-primary text-sm font-ui hover:underline"
                  >
                    Pošalji novu poruku
                  </button>
                </motion.div>
              ) : (
                /* ── Form ── */
                <>
                  <div className="mb-8">
                    <h2 className="font-serif text-2xl font-bold text-foreground mb-2">
                      Pošaljite nam poruku
                    </h2>
                    <p className="text-muted-foreground font-ui text-sm">
                      Popunite formu i javićemo Vam se u najkraćem roku.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="font-ui text-sm text-foreground mb-1.5 block">
                          Ime i prezime <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="name"
                          value={form.name}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          placeholder="Marko Jović"
                          className="font-ui"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="font-ui text-sm text-foreground mb-1.5 block">
                          Telefon <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="phone"
                          value={form.phone}
                          onChange={e => setForm({ ...form, phone: e.target.value })}
                          placeholder="+381601140402"
                          className="font-ui"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="font-ui text-sm text-foreground mb-1.5 block">
                        Poruka <span className="text-destructive">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        value={form.message}
                        onChange={e => setForm({ ...form, message: e.target.value })}
                        placeholder="Npr. Zanima me dostupnost kajmaka i uslovi dostave..."
                        className="font-ui resize-none"
                        rows={5}
                        required
                      />
                    </div>

                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={sending}
                        size="lg"
                        className="w-full sm:w-auto bg-gold-cta hover:bg-gold text-white font-ui font-semibold
                                   shadow-gold px-10 rounded-full transition-all duration-300"
                      >
                        {sending ? (
                          <span className="flex items-center gap-2">
                            <span className="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                            Slanje...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Send size={16} />
                            Pošalji poruku
                          </span>
                        )}
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground font-ui">
                      Polja označena sa <span className="text-destructive">*</span> su obavezna.
                    </p>
                  </form>
                </>
              )}
            </div>

            {/* Quick order note */}
            <div className="mt-5 flex items-start gap-3 p-5 bg-accent/60 rounded-xl border border-border">
              <Phone size={17} className="text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-ui text-sm font-semibold text-foreground mb-0.5">
                  Brza narudžba telefonom
                </p>
                <p className="font-ui text-xs text-muted-foreground leading-relaxed">
                  Možete naručiti i direktno pozivom na{' '}
                  <a href={CONTACT_PHONE_TEL} className="text-primary font-medium hover:underline">{CONTACT_PHONE_DISPLAY}</a>
                  {' '} za hitne narudžbe i hitne slučajeve.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
